/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

/**
 * VNU: nhãn trục dạng huy hiệu chữ cái đầu (SPEC-vnu-khcn-dashboard, CAP-5, C-1).
 *
 * Thiết kế hiện mỗi đơn vị thành viên trên trục X bằng một huy hiệu tròn mang chữ
 * cái đầu của mã đơn vị, màu suy ra từ chính mã đó. Module này dựng phần cấu hình
 * `axisLabel` tương ứng của ECharts.
 *
 * Cố ý viết theo hướng tổng quát — "category badge" chứ không phải "unit logo" —
 * để bất kỳ dashboard nào cũng dùng lại được, đúng yêu cầu cấu hình được của CAP-9.
 * Không có danh sách đơn vị VNU nào được nhúng cứng ở đây.
 */

/**
 * Màu chữ mặc định trên huy hiệu. Nơi gọi nên truyền màu của theme
 * (`theme.colors.grayscale.light5`); hằng số này chỉ là phương án dự phòng cho
 * các nơi gọi không có theme trong tay.
 */
// eslint-disable-next-line theme-colors/no-literal-colors
export const DEFAULT_BADGE_TEXT_COLOR = '#FFFFFF';

/** Bảng màu mặc định cho huy hiệu, lấy từ bảng màu của thiết kế. */
export const DEFAULT_BADGE_COLORS = [
  '#db564d',
  '#3eb546',
  '#e0a316',
  '#8b5cf6',
  '#4a7ded',
  '#edd54a',
];

/**
 * Rút chữ cái đầu từ một nhãn.
 *
 * Chỉ giữ chữ cái Latin: mã đơn vị kiểu "UET", "USSH" cho ra "UE", "US"; nhãn có
 * dấu tiếng Việt hoặc ký tự lạ vẫn ra được thứ gì đó thay vì chuỗi rỗng.
 */
export function categoryInitials(label: string, maxLength = 2): string {
  const letters = (label || '').replace(/[^A-Za-z]/g, '');
  if (letters) {
    return letters.slice(0, maxLength).toUpperCase();
  }
  // Không có chữ Latin nào — dùng ký tự đầu tiên không phải khoảng trắng.
  const fallback = (label || '').trim().slice(0, maxLength);
  return fallback ? fallback.toUpperCase() : '?';
}

/**
 * Chọn màu cho một nhãn một cách tất định.
 *
 * Cùng một nhãn luôn ra cùng một màu, kể cả khi thứ tự dữ liệu thay đổi hay chart
 * được vẽ lại — nếu dùng chỉ số mảng thì đổi bộ lọc là màu nhảy lung tung.
 */
export function categoryColor(
  label: string,
  palette: string[] = DEFAULT_BADGE_COLORS,
): string {
  if (palette.length === 0) {
    return DEFAULT_BADGE_COLORS[0];
  }
  let hash = 0;
  for (let i = 0; i < label.length; i += 1) {
    // Nhân 31 rồi ép về unsigned 32 bit — cùng cách băm chuỗi mà thiết kế dùng.
    hash = (hash * 31 + label.charCodeAt(i)) >>> 0;
  }
  return palette[hash % palette.length];
}

export interface CategoryBadgeAxisLabel {
  formatter: (value: string) => string;
  rich: Record<string, Record<string, unknown>>;
  interval: number;
  margin: number;
}

/**
 * Dựng phần `axisLabel` của ECharts để vẽ nhãn trục thành huy hiệu.
 *
 * ECharts không cho đặt màu nền khác nhau cho từng nhãn qua một style duy nhất,
 * nên phải khai báo sẵn một style `rich` cho mỗi màu trong bảng, rồi formatter
 * chọn style theo nhãn.
 */
export function buildCategoryBadgeAxisLabel(
  palette: string[] = DEFAULT_BADGE_COLORS,
  options: {
    size?: number;
    fontSize?: number;
    maxLength?: number;
    /**
     * Màu chữ trên huy hiệu. Nhận từ ngoài để nơi gọi truyền màu của theme,
     * thay vì nhúng cứng một mã màu trong tiện ích này.
     */
    textColor?: string;
  } = {},
): CategoryBadgeAxisLabel {
  const colors = palette.length > 0 ? palette : DEFAULT_BADGE_COLORS;
  const size = options.size ?? 28;
  const fontSize = options.fontSize ?? 11;
  const maxLength = options.maxLength ?? 2;
  const textColor = options.textColor ?? DEFAULT_BADGE_TEXT_COLOR;

  const rich: Record<string, Record<string, unknown>> = {};
  colors.forEach((color, index) => {
    rich[`badge${index}`] = {
      color: textColor,
      backgroundColor: color,
      width: size,
      height: size,
      lineHeight: size,
      borderRadius: size / 2,
      align: 'center',
      verticalAlign: 'middle',
      fontSize,
      fontWeight: 'bold',
    };
  });

  return {
    formatter: (value: string) => {
      const label = String(value ?? '');
      const color = categoryColor(label, colors);
      const styleName = `badge${colors.indexOf(color)}`;
      return `{${styleName}|${categoryInitials(label, maxLength)}}`;
    },
    rich,
    // Buộc hiện mọi nhãn: huy hiệu chỉ có nghĩa khi đủ mặt các hạng mục, còn
    // ECharts mặc định bỏ bớt nhãn khi chật.
    interval: 0,
    margin: 8,
  };
}

/**
 * Bảng logo theo hạng mục: khoá là ĐÚNG chữ hiện trên trục của chart, giá trị là
 * ảnh (URL hoặc data URI). Đây là dạng người dùng khai báo trong ô cấu hình.
 */
export type CategoryLogoMap = Record<string, string>;

/** Kích thước logo mặc định (px) — bằng đúng bản thiết kế. */
export const DEFAULT_LOGO_SIZE = 32;

/**
 * Đọc bảng logo người dùng nhập. Nhận vào chuỗi JSON (ô cấu hình trên giao diện)
 * hoặc object. Khai báo hỏng thì trả về bảng rỗng — cấu hình là thứ người khác
 * sửa được, không được phép làm vỡ chart.
 */
export function parseCategoryLogos(raw: unknown): CategoryLogoMap {
  if (!raw) return {};
  let value: unknown = raw;
  if (typeof raw === 'string') {
    const text = raw.trim();
    if (!text) return {};
    try {
      value = JSON.parse(text);
    } catch {
      return {};
    }
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return {};
  }
  const out: CategoryLogoMap = {};
  Object.entries(value as Record<string, unknown>).forEach(([key, src]) => {
    // Chỉ nhận ảnh dẫn được: http(s), data URI, hoặc đường dẫn tuyệt đối trong site.
    if (
      typeof src === 'string' &&
      /^(https?:\/\/|data:image\/|\/)/i.test(src.trim())
    ) {
      out[String(key)] = src.trim();
    }
  });
  return out;
}

/**
 * Dựng `axisLabel` hiện NHÃN CHỮ rồi tới LOGO TRÒN ngay bên dưới — đúng thứ tự
 * của bản thiết kế "Quản lý chỉ tiêu". Hạng mục chưa khai báo logo thì rơi về
 * huy hiệu chữ cái đầu, cũng như thiết kế.
 *
 * ECharts không đặt được ảnh nền khác nhau cho từng nhãn qua một style, nên mỗi
 * khoá trong bảng logo phải có sẵn một style `rich` riêng.
 */
export function buildCategoryLogoAxisLabel(
  logos: CategoryLogoMap,
  options: {
    palette?: string[];
    /** Bề rộng logo (px). Không đặt thì lấy 32 như thiết kế. */
    width?: number;
    /** Chiều cao logo (px). Không đặt thì bằng bề rộng — logo vuông/tròn. */
    height?: number;
    fontSize?: number;
    maxLength?: number;
    textColor?: string;
    /** Màu chữ của dòng nhãn (không phải chữ trên huy hiệu). */
    labelColor?: string;
  } = {},
): CategoryBadgeAxisLabel {
  const colors =
    options.palette && options.palette.length > 0
      ? options.palette
      : DEFAULT_BADGE_COLORS;
  const width = Math.max(1, Math.round(options.width ?? DEFAULT_LOGO_SIZE));
  const height = Math.max(1, Math.round(options.height ?? width));
  // Bo tròn theo cạnh ngắn: rộng = cao thì ra hình tròn đúng như thiết kế, còn
  // khi người dùng đổi tỉ lệ thì ra hình viên thuốc chứ không méo.
  const radius = Math.min(width, height) / 2;
  const fontSize = options.fontSize ?? 11;
  const maxLength = options.maxLength ?? 2;
  const textColor = options.textColor ?? DEFAULT_BADGE_TEXT_COLOR;

  const rich: Record<string, Record<string, unknown>> = {
    label: {
      fontSize,
      fontWeight: 'bold',
      align: 'center',
      // Cách dòng giữa chữ và logo, giống khoảng gap 6px của thiết kế.
      padding: [0, 0, 4, 0],
      ...(options.labelColor ? { color: options.labelColor } : {}),
    },
  };

  // Huy hiệu dự phòng cho hạng mục chưa có logo.
  colors.forEach((color, index) => {
    rich[`badge${index}`] = {
      color: textColor,
      backgroundColor: color,
      width,
      height,
      lineHeight: height,
      borderRadius: radius,
      align: 'center',
      verticalAlign: 'middle',
      fontSize,
      fontWeight: 'bold',
    };
  });

  const keys = Object.keys(logos);
  keys.forEach((key, index) => {
    rich[`logo${index}`] = {
      // Thiết kế: hình tròn 32px, ảnh contain. `align`/`verticalAlign` giữ logo
      // căn giữa cả hai chiều, ngay dưới nhãn của cột.
      backgroundColor: { image: logos[key] },
      width,
      height,
      borderRadius: radius,
      align: 'center',
      verticalAlign: 'middle',
    };
  });

  return {
    formatter: (value: string) => {
      const label = String(value ?? '');
      const index = keys.indexOf(label);
      if (index > -1) {
        return `{label|${label}}\n{logo${index}|}`;
      }
      const color = categoryColor(label, colors);
      const badge = `badge${colors.indexOf(color)}`;
      return `{label|${label}}\n{${badge}|${categoryInitials(label, maxLength)}}`;
    },
    rich,
    // Hiện đủ mọi hạng mục: thiếu một cái là mất logo của đơn vị đó.
    interval: 0,
    margin: 8,
  };
}
